import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { History, RotateCcw } from 'lucide-react';
import { getVersionsForIdea } from '../lib/db';

export const VersionHistory = ({ ideaId, onRestore }) => {
    const [versions, setVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);

    useEffect(() => {
        if (!ideaId) return;

        const load = async () => {
            const list = await getVersionsForIdea(ideaId);
            setVersions(list);
            setSelectedVersion(list[0] || null);
        };

        load();
    }, [ideaId]);

    if (!ideaId) return null;

    const formatTimestamp = (value) => {
        try {
            const d = new Date(value);
            return d.toLocaleString();
        } catch {
            return value;
        }
    };

    return (
        <div className="border border-border/60 rounded-xl bg-card/70 backdrop-blur-sm overflow-hidden h-64 flex flex-col">
            <div className="px-4 py-2 border-b border-border/60 flex items-center gap-2 text-xs text-muted-foreground">
                <History className="h-3.5 w-3.5" />
                <span>Version history</span>
            </div>
            <div className="flex flex-1">
                <ScrollArea className="w-1/2 border-r border-border/60">
                    <div className="p-2 space-y-1">
                        {versions.length === 0 && (
                            <p className="text-[11px] text-muted-foreground px-2 py-1">
                                No versions yet. Use &quot;Save version&quot; to capture snapshots.
                            </p>
                        )}
                        {versions.map((v) => (
                            <button
                                key={v.id}
                                type="button"
                                onClick={() => setSelectedVersion(v)}
                                className={`w-full text-left px-2 py-1 rounded-lg text-[11px] leading-snug transition-colors ${
                                    selectedVersion?.id === v.id
                                        ? 'bg-accent text-foreground'
                                        : 'hover:bg-accent/60 text-muted-foreground'
                                }`}
                            >
                                <div className="font-medium truncate">{v.title || 'Untitled'}</div>
                                <div className="opacity-80 truncate">{formatTimestamp(v.timestamp)}</div>
                            </button>
                        ))}
                    </div>
                </ScrollArea>
                <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border/60 text-[11px] text-muted-foreground">
                        <span>{selectedVersion ? 'Preview' : 'Nothing selected'}</span>
                        {selectedVersion && (
                            <Button
                                type="button"
                                size="sm"
                                className="h-7 px-3 rounded-full text-xs"
                                onClick={() => onRestore(selectedVersion)}
                            >
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                Restore
                            </Button>
                        )}
                    </div>
                    <ScrollArea className="flex-1">
                        <div className="p-3 text-xs whitespace-pre-wrap text-muted-foreground leading-relaxed">
                            {selectedVersion?.content || 'Select a version to preview its content.'}
                        </div>
                    </ScrollArea>
                </div>
            </div>
        </div>
    );
};
